import type { ComponentType } from "react";

/**
 * Stale-chunk handling for lazily imported route modules.
 *
 * Every route is a content-hashed `React.lazy(() => import(...))` chunk. On each
 * deploy the previous chunks are removed from the host. A client that still holds
 * an old `index.html` (e.g. a tab left open across a deploy) will request a chunk
 * URL that no longer exists; with an SPA rewrite the host answers the missing
 * `.js` with `index.html` (`Content-Type: text/html`), and the browser refuses to
 * execute it as a module script. Browsers word this failure differently, so we
 * match all known variants rather than a single message.
 */
const DYNAMIC_IMPORT_ERROR_PATTERNS = [
  // Chromium
  "Failed to fetch dynamically imported module",
  // Firefox
  "error loading dynamically imported module",
  // Safari
  "Importing a module script failed",
  // MIME-type mismatch when the host serves index.html for a missing chunk
  "is not a valid JavaScript MIME type",
  "Expected a JavaScript module script",
  // Vite preload helper (chunk + its CSS)
  "Unable to preload CSS",
] as const;

export const isDynamicImportError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : typeof error === "string" ? error : "";

  return DYNAMIC_IMPORT_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
};

/**
 * Resolves a named export from a lazily imported route module for use with
 * `React.lazy`.
 *
 * A stale client that loaded an old `index.html` across a deploy can end up
 * fetching a shared/entry chunk that no longer carries the export the route
 * expects. The dynamic import then *resolves* to a module namespace without the
 * named export, and the old `const { X } = await import(...)` form throws a bare
 * `TypeError: Cannot destructure property 'X' ... as it is undefined` — which
 * `isDynamicImportError` does not recognise, so the stale-chunk auto-reload never
 * fires and users are stuck.
 *
 * By throwing a message that `isDynamicImportError` matches, this routes the same
 * root cause through the existing reload-once recovery in `RouteErrorBoundary`.
 */
export const importNamedComponent = async (
  factory: () => Promise<Record<string, unknown>>,
  exportName: string
): Promise<{ default: ComponentType<unknown> }> => {
  const module = await factory();
  const Component = module?.[exportName];

  if (!Component) {
    throw new Error(`Failed to fetch dynamically imported module: missing export "${exportName}"`);
  }

  return { default: Component as ComponentType<unknown> };
};

const RELOAD_TIMESTAMP_KEY = "staleChunkReloadAt";
const RELOAD_THROTTLE_MS = 15_000;

/**
 * Reloads the page once to fetch the freshly deployed chunks. Throttled via
 * sessionStorage so a genuinely broken chunk can't trigger a reload loop — if a
 * reload was attempted within the throttle window we bail out and let the caller
 * surface a fallback UI instead.
 *
 * @returns `true` if a reload was triggered, `false` if throttled.
 */
export const reloadForStaleChunkOnce = (): boolean => {
  try {
    const lastReloadAt = Number(window.sessionStorage.getItem(RELOAD_TIMESTAMP_KEY) ?? 0);

    if (Date.now() - lastReloadAt < RELOAD_THROTTLE_MS) {
      return false;
    }

    window.sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, String(Date.now()));
  } catch {
    // sessionStorage can throw (private mode / disabled storage); reload anyway.
  }

  window.location.reload();
  return true;
};
